import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { Heart, MapPin, Phone, ExternalLink, Loader2, Images } from "lucide-react";
import FavoriteButton from "@/components/dashboard/FavoriteButton";
import WhatsAppButton from "@/components/dashboard/WhatsAppButton";
import ProfileDetailDialog from "@/components/dashboard/ProfileDetailDialog";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85";

function fmtPhone(p) {
  return p ? p.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3") : "—";
}

export default function FavoritesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { Promise.resolve().then(load); }, [load]);

  if (loading) {
    return (
      <div className="text-center py-16 text-[#71717a] text-sm flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 spin text-[#c8aa6e]" /> A carregar favoritos…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-[#18181b] border border-[#27272a] px-6 py-16 text-center" data-testid="favorites-empty">
        <Heart className="w-10 h-10 text-[#3f3f46] mx-auto mb-4" />
        <h3 className="font-head text-lg text-[#fafafa] mb-1">Ainda não tens favoritos</h3>
        <p className="text-sm text-[#71717a]">Clica no coração em qualquer perfil (Pesquisa IA, Perfis ou detalhe) para o guardar aqui.</p>
      </div>
    );
  }

  return (
    <div data-testid="favorites-view">
      <div className="label-tiny mb-3">{items.length} perfil(is) guardado(s)</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((p, i) => (
          <div key={p.id} data-testid={`favorite-card-${i}`} className="bg-[#18181b] border border-[#27272a] hover:border-[#3f3f46] transition-colors">
            <div className="relative cursor-pointer" onClick={() => setDetailId(p.id)} title="Ver galeria e detalhes">
              <img
                src={imgProxy(p.photo_url, 480) || PLACEHOLDER}
                onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                alt={p.name}
                loading="lazy"
                decoding="async"
                className="w-full aspect-[3/4] object-cover bg-[#27272a]"
              />
              {p.photos && p.photos.length > 1 && (
                <span className="absolute bottom-2 right-2 text-[11px] bg-black/70 text-[#e4e4e7] px-2 py-0.5 inline-flex items-center gap-1">
                  <Images className="w-3 h-3" /> {p.photos.length}
                </span>
              )}
              <span className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                <FavoriteButton profileId={p.id} initial onToggle={(v) => { if (!v) load(); }} size={18} className="bg-black/50 p-1.5 rounded-full" />
              </span>
            </div>
            <div className="p-3">
              <div className="font-head text-base text-[#fafafa] truncate">{p.name || "—"}</div>
              <div className="text-xs text-[#71717a] mb-2 inline-flex items-center gap-1">
                {p.source_name} · <MapPin className="w-3 h-3" />{p.city || "—"}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-[#fafafa] inline-flex items-center gap-1">
                  <Phone className="w-3 h-3 text-[#71717a]" />{fmtPhone(p.phone)}
                </span>
                <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  {p.phone && <WhatsAppButton phone={p.phone} name={p.name} size={16} />}
                  {p.profile_url && (
                    <a href={p.profile_url} target="_blank" rel="noreferrer" className="text-[#c8aa6e] hover:text-[#d4b982]" onClick={(e) => e.stopPropagation()}>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ProfileDetailDialog profileId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
